import Graphql from '@/util/GraphQL'
import PromiseWorker from 'promise-worker'

const workerCombinationWithLowestIncreamentalCost = new PromiseWorker(new Worker('/workers/combination_with_lowest_increamental_cost.js'))

export const getComplianceCostRange = async ({ studyDataArgs, minTargetScore, measuresExcludedIds, measuresMandatoryIds, debug }) => {
    
  const studyDataRows = await getInitialStudyData({ studyDataArgs, measuresExcludedIds, measuresMandatoryIds })
  const methodOnePermutation = await workerCombinationWithLowestIncreamentalCost.postMessage({ studyDataRows: [...studyDataRows], minTargetScore, shouldExcludeMeasuresContingent : false, debug })
  const methodTwoPermutation = await workerCombinationWithLowestIncreamentalCost.postMessage({ studyDataRows: [...studyDataRows], minTargetScore, shouldExcludeMeasuresContingent : true, debug })
  
  return Promise.all([
    methodOnePermutation,
    methodTwoPermutation
  ]).then((methods => {
    return {
      studyDataRows: [...studyDataRows],
      methods: [methods[0], methods[1]],
    }
  }))
}

export const getComplianceCostRangeImpacts = async ({ studyDataArgs, minTargetScore, measuresExcludedIds, measuresMandatoryIds }) => {

  const studyDataRows = await getInitialStudyData({ studyDataArgs, measuresExcludedIds, measuresMandatoryIds })
  
  const getTheNextTargetScoreImpact = async (targetScore, shouldExcludeMeasuresContingent, output = {}) => {
    if (targetScore <= minTargetScore) {
      const { lowestCombinationIncrementalCost, combinationWithLowestIncrementalCost } = await workerCombinationWithLowestIncreamentalCost.postMessage({ studyDataRows, minTargetScore : targetScore, shouldExcludeMeasuresContingent })
      const targetScoreFounded = getCombinationTargetScore(combinationWithLowestIncrementalCost)
      while ( (targetScore <= targetScoreFounded) && (targetScore <= minTargetScore)) {
        output[targetScore] = lowestCombinationIncrementalCost
        targetScore++
      }
      return getTheNextTargetScoreImpact(targetScore, shouldExcludeMeasuresContingent, output)
    } else {
      return output
    }
  }

  return Promise.all([getTheNextTargetScoreImpact(1, false), getTheNextTargetScoreImpact(1, true)])
}

export const getInitialStudyData = async ({ studyDataArgs, measuresExcludedIds, measuresMandatoryIds }) => {
  let studyDataRows = await getStudyData(studyDataArgs)    
  studyDataRows = studyDataRows.filter(study_data => {
    return measuresExcludedIds.includes(study_data.measure_id) // Remove Excluded Measures 
      || measuresMandatoryIds.includes(study_data.measure_id) // Remove Mandatory Measures 
      || getMeasureScore(study_data) <= 0 // Remove Measures with Negative Measure Score
      || study_data.measure.isFullyRepresentedPackage // Remove Fully Represented Packages
      || study_data.measure.hide_in_flexible_path
      ? false : true
  })
  return studyDataRows.map(study_data => {
    study_data.measure.isFullyRepresentedPackage = checkIsFullyRepresentedPackage(study_data, studyDataRows)
    return study_data
  })
}

export const getStudyData = ({ vintage_id, vintage_ids, prototype_id, climate_zone_raw }) => {
  vintage_ids = vintage_ids ?? [ vintage_id ]
  vintage_ids = vintage_ids.map(id => `"${id}"`).join(',')

  const query = `
    {
      study_data (
        where: [
          {column: "prototype_id", operation: "=", value: "${prototype_id}"},
          {column: "climate_zone_raw", operation: "=", value: "${climate_zone_raw}"},
        ],
        whereIn: [
          { column: "vintage_id", value: [${vintage_ids}]}
        ],
        orderBy: [ { column: "initial_cost" , order: "asc"}]
      ){
        id
        study_row_number
        climate_zone_raw
        study_id
        prototype_id
        measure_id
        measure {
          id 
          title
          is_package
          is_contingent
          exclude_measures
          hide_in_flexible_path
          is_other_child_measures
          measures {
            id
            title
          }
        }
        vintage_id
        fuel_id
        baseline_fuel_type
        initial_cost
        kwh_savings
        therms_savings
        annual_bill_savings
        simple_payback
        tdv_benefit_to_cost_ratio
        on_bill_cost_ratio
        emissions_savings
        emissions_savings_pct
        compliance_margin
        lifecycle_savings
        tdv2022_benefit_to_cost_ratio
        energy_savings_combined
      }
    }
  `
  return Graphql({ query })
  .then(({ data }) => {
    return data.study_data      
  })
}

// https://epcgo.atlassian.net/wiki/spaces/BL/pages/598540289/Compliance+Costs+Range+Calculations
export const checkIsFullyRepresentedPackage = (study_data, studyDataRows) => {
  
  if (study_data.measure.is_other_child_measures || !study_data.measure.is_package) return false
  
  const singleMeasuresIds = study_data.measure.measures.map(m => m.id)
  const studyDataRowsDic = studyDataRows.reduce((acc, studyData) => {
    acc[studyData.measure_id] = true
    return acc
  }, {})
  
  for (const measure_id of singleMeasuresIds) {
    if (!studyDataRowsDic[measure_id]) return false
  }
  
  return true

}

export const roundToRange = (number) => {
  const n = Math.round(number)
  const digits = n.toString().length
  const digitsToWithdraw = n < 3 ? 0 : digits - 2
  return (n.toString().substring(0, digits - digitsToWithdraw)).padEnd(digits, '0')
}

export const formatImpactRange = (targetScore, range) => {
  const minValue = range[0][targetScore]
  const maxValue = range[1][targetScore]
  return minValue !== maxValue ? `$${roundToRange(minValue)} - $${roundToRange(maxValue)}` : `$${roundToRange(minValue)}`
}


// Method is also implemented at workers/combination_with_lowest_incremental_costs.js
// Please, update it there if you update here
export const getCombinationTargetScore = (combination) => {
  return combination.reduce((acc, study_data) => {        
    return acc + getMeasureScore(study_data)
  }, 0)
}

// Method is also implemented at workers/combination_with_lowest_incremental_costs.js
// Please, update it there if you update here
export const getCombinationIncrementalCost = (combination) => {
  return Math.round(combination.reduce((acc, study_data) => {        
    return acc + study_data.initial_cost
  }, 0))
}
// Method is also implemented at workers/combination_with_lowest_incremental_costs.js
// Please, update it there if you update here
export const getMeasureScore = (study_data) => {
  return Math.round(study_data.energy_savings_combined)
}

export default {
  getComplianceCostRange,
  getComplianceCostRangeImpacts,
  getCombinationTargetScore,
  getCombinationIncrementalCost,
  getInitialStudyData,
  getMeasureScore,
  getStudyData,
  checkIsFullyRepresentedPackage,
  roundToRange,
  formatImpactRange
}
